import type { ThreadMessage } from "@assistant-ui/core";

import type { ICitationResolution } from "@/api/chat/chat.types";
import type { ChatTableData } from "@/components/assistant-ui/utils/parse-markdown-table";
import { parseTablesFromMarkdown } from "@/components/assistant-ui/utils/parse-tables-from-markdown";

/** A table paired with the citation resolutions of the message it came from,
 * so the export can resolve that table's native cite:// handles against the
 * right (per-message) map. */
export interface ExtractedTable {
    table: ChatTableData;
    resolutions: ICitationResolution[];
}

/**
 * Walk a thread's messages in order and return every exportable table from
 * the assistant turns, flattened. Mirrors what the renderer shows: only
 * assistant messages produce interactive `ChatTable`s, and only their text
 * parts carry markdown (tool-call / reasoning / source parts are runtime
 * artifacts, not content; same filter as `threadToMarkdown`).
 *
 * Each *text part* is parsed independently, never as one joined document.
 * The renderer mounts a separate react-markdown document per content part,
 * so a heading at the end of one part can't title a table in the next, and
 * a table split across a tool-call boundary renders as two unparseable
 * halves (and is therefore not exportable). Joining the parts first would
 * break both invariants: it could mis-infer a stale heading as a title or
 * "reconstruct" a table the user never saw as interactive. Parsing per part
 * reproduces exactly what react-markdown renders.
 *
 * Each table is paired with its source message's `citation_resolutions` so a
 * cite:// handle resolves against the map of the turn that authored it (the
 * registry is rebuilt per turn, so handle `cN` means different things across
 * turns).
 */
export function extractTablesFromMessages(
    messages: readonly ThreadMessage[]
): ExtractedTable[] {
    const tables: ExtractedTable[] = [];
    for (const msg of messages) {
        if (msg.role !== "assistant") continue;
        const content = msg.content;
        if (!content) continue;
        const resolutions =
            (msg.metadata?.custom?.citation_resolutions as
                | ICitationResolution[]
                | undefined) ?? [];
        for (const part of content) {
            if (part.type !== "text" || !part.text) continue;
            for (const table of parseTablesFromMarkdown(part.text)) {
                tables.push({ table, resolutions });
            }
        }
    }
    return tables;
}
